import DailyRewardsService from '../services/DailyRewardsService/DailyRewardsService';
import { DailyReward, DailyScratcherState } from './dailyScratcher.types';
import {
  appLogout,
  createSlice,
  injectSlice,
  createAsyncThunk,
} from '@betty/toolkit/state';
import { ApiError } from '@betty/toolkit/api';
import DailyScratcherService from '../services/DailyScratcherService/DailyScratcherService';

const initialState: DailyScratcherState = {
  data: null,
  dataState: 'idle',
  launchDailyScratcherData: null,
  launchDailyScratcherDataState: 'idle',
  shouldShowAnimatedCoins: false,
};

export const launchDailyScratcher = createAsyncThunk(
  'dailyScratcher/launch',
  async (rewardId: number) => {
    return await DailyScratcherService.launchDailyScratcher({
      pathParams: { id: rewardId },
    });
  },
);
export const fetchDailyScratcher = createAsyncThunk<
  DailyReward,
  void,
  { rejectValue: string | ApiError }
>('dailyRewards/fetch', async (_, { rejectWithValue }) => {
  try {
    const rewards = await DailyRewardsService.getDailyRewards();
    const dailyScratcher = rewards.dailyRewards.find(
      (reward) => reward.type === 'DailyScratcher',
    );

    if (!dailyScratcher) {
      return rejectWithValue('DailyScratcher reward not found');
    }

    return dailyScratcher;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error);
    }

    throw error;
  }
});

const dailyScratcher = createSlice({
  name: 'dailyScratcher',
  initialState,
  reducers: {
    triggerAnimatedCoins: (state) => {
      state.shouldShowAnimatedCoins = true;
    },
    resetAnimatedCoins: (state) => {
      state.shouldShowAnimatedCoins = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDailyScratcher.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchDailyScratcher.fulfilled, (state, action) => {
      state.data = action.payload;
      state.dataState = 'fulfilled';
    });

    builder.addCase(fetchDailyScratcher.rejected, (state) => {
      state.dataState = 'rejected';
    });
    builder.addCase(launchDailyScratcher.pending, (state) => {
      state.launchDailyScratcherDataState = 'pending';
    });
    builder.addCase(launchDailyScratcher.fulfilled, (state, action) => {
      state.launchDailyScratcherData = action.payload;
      state.launchDailyScratcherDataState = 'fulfilled';
    });
    builder.addCase(launchDailyScratcher.rejected, (state) => {
      state.launchDailyScratcherDataState = 'rejected';
    });
    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

export const withDailyScratcher = injectSlice({
  reducerPath: 'dailyScratcher',
  reducer: dailyScratcher.reducer,
});

export const selectDailyScratcher = withDailyScratcher.selector(
  (state) => state.dailyScratcher?.data,
);
export const selectDailyScratcherStatus = withDailyScratcher.selector(
  (state) => state.dailyScratcher?.data?.status,
);

export const selectDailyScratcherPlayerRewardId = withDailyScratcher.selector(
  (state) => state.dailyScratcher?.data?.playerRewardId,
);

export const selectDailyScratcherLaunchUrl = withDailyScratcher.selector(
  (state) => state.dailyScratcher?.launchDailyScratcherData?.url,
);

export const selectDailyScratcherDataState = withDailyScratcher.selector(
  (state) => state.dailyScratcher?.dataState,
);

export const selectShouldShowAnimatedCoins = withDailyScratcher.selector(
  (state) => state.dailyScratcher?.shouldShowAnimatedCoins || false,
);

export const { triggerAnimatedCoins, resetAnimatedCoins } =
  dailyScratcher.actions;

declare global {
  interface LazyLoadedSlices {
    dailyScratcher: ReturnType<typeof dailyScratcher.reducer>;
  }
}
